import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import TinyTimCostTile from './TinyTimCostTile';
import TinyTimToolHistory from './TinyTimToolHistory';
import ResearchLibraryDrawer from './ResearchLibraryDrawer';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_COLORS = {
  success: 'bg-emerald-500/20 text-emerald-400',
  error: 'bg-red-500/20 text-red-400',
  running: 'bg-blue-500/20 text-blue-400',
  unknown: 'bg-slate-500/20 text-slate-400',
};

const AgentCard = ({ agent, onRunNow }) => {
  const [triggering, setTriggering] = useState(false);

  const handleRun = async () => {
    setTriggering(true);
    try {
      await onRunNow(agent.id);
    } finally {
      setTimeout(() => setTriggering(false), 2000);
    }
  };

  const statusClass = STATUS_COLORS[agent.last_status] || STATUS_COLORS.unknown;
  const lastRun = agent.last_run
    ? new Date(agent.last_run).toLocaleString()
    : 'Never';
  const nextRun = agent.next_run
    ? new Date(agent.next_run).toLocaleString()
    : 'Not scheduled';

  return (
    <div
      className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg border border-slate-700/50 hover:border-slate-600/50 transition-colors"
      data-testid={`agent-card-${agent.id}`}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-white font-medium text-sm truncate">{agent.name}</span>
          <span className={`px-2 py-0.5 rounded text-xs font-medium ${statusClass}`}>
            {agent.last_status}
          </span>
        </div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-400">
          <span>Last: {lastRun}</span>
          <span>Next: {nextRun}</span>
          {agent.last_duration_s != null && (
            <span>Duration: {agent.last_duration_s.toFixed(1)}s</span>
          )}
        </div>
        {agent.last_error && (
          <p className="text-xs text-red-400 mt-1 truncate">{agent.last_error}</p>
        )}
      </div>
      <button
        onClick={handleRun}
        disabled={triggering}
        className={`ml-4 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
          triggering
            ? 'bg-blue-500/20 text-blue-300 cursor-wait'
            : 'bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20'
        }`}
        data-testid={`run-now-${agent.id}`}
      >
        {triggering ? 'Triggered...' : 'Run Now'}
      </button>
    </div>
  );
};

const CATEGORY_ICONS = {
  'Discovery Agents': 'search',
  'Scoring & Validation': 'bar-chart-2',
  'Ghost Recon': 'radar',
  'Performance Data': 'trending-up',
  'Predictions': 'compass',
  'Briefing': 'send',
  'Other': 'cpu',
};

const CATEGORY_COLORS = {
  'Discovery Agents': 'text-blue-400',
  'Scoring & Validation': 'text-emerald-400',
  'Ghost Recon': 'text-purple-400',
  'Performance Data': 'text-amber-400',
  'Predictions': 'text-cyan-400',
  'Briefing': 'text-orange-400',
  'Other': 'text-slate-400',
};

const DigestCard = ({ digest }) => {
  const [expanded, setExpanded] = useState(false);
  const genDate = digest.generated_at
    ? new Date(digest.generated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : 'Unknown';
  const weekStart = digest.week_start
    ? new Date(digest.week_start).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : '';
  const weekEnd = digest.week_end
    ? new Date(digest.week_end).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    : '';

  return (
    <div className="bg-slate-800/50 rounded-lg border border-slate-700/50 overflow-hidden" data-testid="digest-card">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-slate-700/30 transition-colors"
        data-testid="digest-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-orange-500/20 flex items-center justify-center text-orange-400 text-sm font-bold">
            {digest.sections_count || 0}
          </div>
          <div>
            <p className="text-sm text-white font-medium">Week of {weekStart} – {weekEnd}</p>
            <p className="text-xs text-slate-400">Generated {genDate} · {digest.sections_count || 0} sections</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {(digest.section_titles || []).slice(0, 3).map((t, i) => (
            <span key={i} className="hidden sm:inline-block px-2 py-0.5 bg-slate-700/50 text-xs text-slate-300 rounded">{t}</span>
          ))}
          <svg className={`w-4 h-4 text-slate-400 transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>
      {expanded && (
        <div className="px-4 pb-4 border-t border-slate-700/30">
          <div
            className="mt-3 text-sm text-slate-300 leading-relaxed prose prose-invert prose-sm max-w-none [&_b]:text-white [&_i]:text-slate-200"
            dangerouslySetInnerHTML={{ __html: digest.digest_text || '<p class="text-slate-500">No content available.</p>' }}
            data-testid="digest-content"
          />
        </div>
      )}
    </div>
  );
};

export const OperationsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [apiUsage, setApiUsage] = useState(null);
  const [checkpoints, setCheckpoints] = useState([]);
  const [usagePeriod, setUsagePeriod] = useState('weekly');
  const [digests, setDigests] = useState([]);
  const [digestsLoading, setDigestsLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [circuitBreaker, setCircuitBreaker] = useState(null);

  const fetchStatus = useCallback(async () => {
    try {
      const [statusRes, usageRes, cpRes, cbRes] = await Promise.all([
        axios.get(`${API}/agents/status`),
        axios.get(`${API}/api-usage/summary`).catch(() => ({ data: null })),
        axios.get(`${API}/agent-checkpoints`).catch(() => ({ data: { checkpoints: [] } })),
        axios.get(`${API}/circuit-breaker/status`).catch(() => ({ data: null })),
      ]);
      setData(statusRes.data);
      setApiUsage(usageRes.data);
      setCheckpoints(cpRes.data.checkpoints || []);
      setCircuitBreaker(cbRes.data);
      setLastRefresh(new Date());
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch agent status');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDigests = useCallback(async () => {
    setDigestsLoading(true);
    try {
      const res = await axios.get(`${API}/digest/history?limit=20`);
      setDigests(res.data.digests || []);
    } catch {
      // silently ignore
    } finally {
      setDigestsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    fetchDigests();
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, [fetchStatus, fetchDigests]);

  const handleGenerateDigest = async () => {
    setGenerating(true);
    try {
      await axios.post(`${API}/digest/generate`);
      await fetchDigests();
    } catch (err) {
      console.error('Failed to generate digest:', err);
    } finally {
      setGenerating(false);
    }
  };

  const handleRunNow = async (jobId) => {
    try {
      await axios.post(`${API}/agents/${jobId}/run`);
      setTimeout(fetchStatus, 3000);
    } catch (err) {
      console.error('Failed to trigger agent:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-6 text-center">
        <p className="text-red-400 font-medium">{error}</p>
        <button onClick={fetchStatus} className="mt-3 text-sm text-slate-400 hover:text-white">
          Retry
        </button>
      </div>
    );
  }

  const categories = data?.categories || {};
  const totalJobs = data?.total_jobs || 0;
  const schedulerRunning = data?.scheduler_running || false;

  return (
    <div className="space-y-8" data-testid="operations-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white" data-testid="operations-title">
            Operations
          </h2>
          <p className="text-slate-400 mt-1">
            Agent Activity Monitor &mdash; {totalJobs} agents
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <span
              className={`w-2 h-2 rounded-full ${schedulerRunning ? 'bg-emerald-400 animate-pulse' : 'bg-red-400'}`}
            ></span>
            <span className="text-sm text-slate-400">
              Scheduler {schedulerRunning ? 'Running' : 'Stopped'}
            </span>
          </div>
          <button
            onClick={fetchStatus}
            className="px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-sm text-slate-300 hover:text-white hover:border-slate-600 transition-colors"
            data-testid="refresh-agents-btn"
          >
            Refresh
          </button>
        </div>
      </div>

      {lastRefresh && (
        <p className="text-xs text-slate-500">
          Last refreshed: {lastRefresh.toLocaleTimeString()} (auto-refreshes every 30s)
        </p>
      )}

      {/* Tiny Tim cost tile + tool history (P3) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <TinyTimCostTile days={7} />
        <TinyTimToolHistory limit={20} />
      </div>

      {/* Research Library — research briefs + ABH CI (P5.5) */}
      <ResearchLibraryDrawer limit={12} />

      {/* Circuit Breaker Status */}
      {circuitBreaker && (
        <div
          className={`rounded-xl p-4 border ${
            circuitBreaker.is_tripped
              ? 'bg-red-500/10 border-red-500/30'
              : 'bg-slate-900/50 border-slate-800'
          }`}
          data-testid="circuit-breaker-section"
        >
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${circuitBreaker.is_tripped ? 'bg-red-400 animate-pulse' : 'bg-emerald-400'}`} />
              <div>
                <p className="text-sm font-medium text-white">
                  Claude API Circuit Breaker {circuitBreaker.is_tripped ? '— TRIPPED' : '— Active'}
                </p>
                <p className="text-xs text-slate-400">
                  Daily spend: <span className={circuitBreaker.is_tripped ? 'text-red-400 font-medium' : 'text-emerald-400'}>${circuitBreaker.daily_spend.toFixed(4)}</span>
                  {' / '}
                  <span className="text-slate-300">${circuitBreaker.hard_cap.toFixed(2)} cap</span>
                  {circuitBreaker.is_tripped && (
                    <span className="text-red-400 ml-2">
                      Resets in {Math.floor(circuitBreaker.resets_in_seconds / 3600)}h {Math.floor((circuitBreaker.resets_in_seconds % 3600) / 60)}m
                    </span>
                  )}
                </p>
              </div>
            </div>
            {!circuitBreaker.is_tripped && circuitBreaker.hard_cap > 0 && (
              <div className="w-32 h-2 bg-slate-700 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${Math.min(100, (circuitBreaker.daily_spend / circuitBreaker.hard_cap) * 100)}%`,
                    backgroundColor: (circuitBreaker.daily_spend / circuitBreaker.hard_cap) > 0.8 ? '#f59e0b' : '#F59E0B',
                  }}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {Object.entries(categories).map(([category, agents]) => (
        <div key={category} className="bg-slate-900/50 border border-slate-800 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <span className={`text-lg font-semibold ${CATEGORY_COLORS[category] || 'text-white'}`}>
              {category}
            </span>
            <span className="text-xs text-slate-500 bg-slate-800 px-2 py-0.5 rounded-full">
              {agents.length} agent{agents.length !== 1 ? 's' : ''}
            </span>
          </div>
          {agents.length === 0 ? (
            <p className="text-slate-500 text-sm">No agents in this category</p>
          ) : (
            <div className="space-y-2">
              {agents.map((agent) => (
                <AgentCard key={agent.id} agent={agent} onRunNow={handleRunNow} />
              ))}
            </div>
          )}
        </div>
      ))}

      {/* API Usage Section */}
      {apiUsage && (
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6" data-testid="api-usage-section">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-white">API Usage</h3>
            <div className="flex gap-1 bg-slate-800 rounded-lg p-0.5">
              {['daily', 'weekly', 'monthly'].map(p => (
                <button key={p} onClick={() => setUsagePeriod(p)}
                  className={`px-3 py-1 text-xs rounded-md transition-colors ${usagePeriod === p ? 'bg-emerald-600 text-white' : 'text-slate-400 hover:text-white'}`}
                  data-testid={`usage-period-${p}`}
                >{p.charAt(0).toUpperCase() + p.slice(1)}</button>
              ))}
            </div>
          </div>
          {apiUsage[usagePeriod] && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/30">
                <p className="text-xs text-slate-400 mb-1">Anthropic (Claude)</p>
                <p className="text-2xl font-bold text-emerald-400" data-testid="anthropic-cost">
                  ${apiUsage[usagePeriod].anthropic.total_cost_usd.toFixed(4)}
                </p>
                <p className="text-xs text-slate-500">{apiUsage[usagePeriod].anthropic.total_calls} calls / {apiUsage[usagePeriod].anthropic.total_tokens.toLocaleString()} tokens</p>
                {Object.entries(apiUsage[usagePeriod].anthropic.by_agent || {}).length > 0 && (
                  <div className="mt-2 space-y-1">
                    {Object.entries(apiUsage[usagePeriod].anthropic.by_agent).slice(0, 5).map(([agent, cost]) => (
                      <div key={agent} className="flex justify-between text-xs">
                        <span className="text-slate-400 truncate">{agent}</span>
                        <span className="text-slate-300">${cost.toFixed(4)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700/30">
                <p className="text-xs text-slate-400 mb-1">ScraperAPI</p>
                <p className="text-2xl font-bold text-blue-400" data-testid="scraper-credits">
                  {apiUsage[usagePeriod].scraperapi.total_credits} credits
                </p>
                <p className="text-xs text-slate-500">{apiUsage[usagePeriod].scraperapi.total_calls} calls</p>
                {Object.entries(apiUsage[usagePeriod].scraperapi.by_agent || {}).length > 0 && (
                  <div className="mt-2 space-y-1">
                    {Object.entries(apiUsage[usagePeriod].scraperapi.by_agent).slice(0, 5).map(([agent, credits]) => (
                      <div key={agent} className="flex justify-between text-xs">
                        <span className="text-slate-400 truncate">{agent}</span>
                        <span className="text-slate-300">{credits} credits</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Checkpoints Section */}
      {checkpoints.length > 0 && (
        <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6" data-testid="checkpoints-section">
          <h3 className="text-lg font-semibold text-white mb-4">Agent Checkpoints</h3>
          <div className="space-y-2">
            {checkpoints.map(cp => (
              <div key={cp.agent_id} className={`p-3 rounded-lg border ${cp.is_stuck ? 'border-red-500/30 bg-red-500/5' : 'border-slate-700/50 bg-slate-800/50'}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-white font-medium">{cp.agent_id}</span>
                  {cp.is_stuck && <span className="text-xs text-red-400 font-bold">STUCK</span>}
                </div>
                <div className="text-xs text-slate-400 mt-1">
                  Batch: {cp.batch_index || 0} / Processed: {(cp.processed_ids || []).length} items
                  {cp.consecutive_failures > 0 && <span className="text-amber-400 ml-2">({cp.consecutive_failures} failures)</span>}
                </div>
                {cp.last_error && <p className="text-xs text-red-400 mt-1 truncate">{cp.last_error}</p>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Past Digests Section */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-6" data-testid="past-digests-section">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-white">Weekly Intelligence Digests</h3>
            <p className="text-xs text-slate-400 mt-0.5">AI-generated briefs summarizing discovery, Ghost Recon, creative DNA, predictions &amp; seasonal data</p>
          </div>
          <button
            onClick={handleGenerateDigest}
            disabled={generating}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              generating
                ? 'bg-orange-500/20 text-orange-300 cursor-wait'
                : 'bg-orange-500/10 text-orange-400 hover:bg-orange-500/20 border border-orange-500/30'
            }`}
            data-testid="generate-digest-btn"
          >
            {generating ? 'Generating...' : 'Generate Now'}
          </button>
        </div>

        {digestsLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-orange-500"></div>
          </div>
        ) : digests.length === 0 ? (
          <div className="text-center py-8 bg-slate-800/30 rounded-lg border border-dashed border-slate-700">
            <p className="text-slate-400 text-sm">No digests generated yet</p>
            <p className="text-slate-500 text-xs mt-1">Click "Generate Now" to create your first weekly intelligence brief</p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="digests-list">
            {digests.map((digest, idx) => (
              <DigestCard key={idx} digest={digest} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OperationsPage;
